import React, { useState, useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import * as routes from 'routes';

import Button from '@material-ui/core/Button';

import { MTableToolbar, Column } from '@material-table/core';
import useStyles, { rightCellStyles, centerCellStyles } from './styles/Accounts';

import {
  compareAccountDates,
  formatAmountValue,
  searchInStrings,
  compareAmountValues,
  compareCzechDates,
  fractionToPercent,
} from 'helpers/helpers';
import IconButton from '@material-ui/core/IconButton';
import DetailIcon from '@material-ui/icons/DescriptionOutlined';
import NotificationImportantIcon from '@material-ui/icons/NotificationImportant';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Theme } from '@material-ui/core/styles/createTheme';
import Grid from '@material-ui/core/Grid';
import GetAppIcon from '@material-ui/icons/GetApp';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';

import MaterialTable from 'components/Table/Table';
import Tooltip from 'components/Tooltip/Tooltip';
import Info from 'components/Tooltip/Info';
import NoteIcon from 'components/Icons/Note';
import FilterIcon from 'components/Icons/FilterIcon';
import LoadingIndicator from 'components/Loading/LoadingIndicator';
import GenerationDialog from 'components/GenerationDialog/GenerationDialog';

import { AccountPageTypes, AccountsStore, overdue, Portfolio } from 'stores/accountsStore';
import { Widths } from './Accounts';
import { RootStore } from 'stores/rootStore';
import { EdwardColors, AccountStatus, Order } from 'enums';
import ExpectedDeposits from './ExpectedDeposits/ExpectedDeposits';

interface Props {
  newAccountsStore?: AccountsStore;
  rootStore?: RootStore;
}

const ImportantDates: React.FC<Props> = ({ newAccountsStore, rootStore }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const {
    dates,
    pageSize,
    onChangeRowsPerPage,
    downloadAccounts,
    onTableSearchChange,
    accountsState,
    onTableOrderChange,
    sortColumns,
    onColumnDragged,
    onChangeColumnHidden,
    reloadAccounts,
  } = newAccountsStore!;
  const [filtersVisible, setFilterVisible] = useState(false);
  const state = accountsState[AccountPageTypes.DATES];
  const [defaultView, setDefaultView] = useState(!newAccountsStore?.accountsState[AccountPageTypes.DATES].cols);
  const [expectedDepositsId, setExpectedDepositsId] = useState<string | null>(null);

  const toggleFilterVisibility = () => {
    setFilterVisible(!filtersVisible);
  };

  useEffect(() => {
    rootStore!.stores.uiStore.setAccountsListData({ newAccountsStore, tab: AccountPageTypes.DATES });
    return () => {
      rootStore!.stores.uiStore.unsetAccountsListData();
    };
  });

  const userModified = newAccountsStore?.accountsState[AccountPageTypes.DATES].userModified;

  useEffect(() => {
    setDefaultView(!newAccountsStore?.accountsState[AccountPageTypes.DATES].userModified);
  }, [newAccountsStore?.accountsState, userModified]);

  const tablet = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'), { noSsr: true });
  const mid = useMediaQuery('(min-width:685px)', { noSsr: true });
  const laptop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'), { noSsr: true });
  const desktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'), { noSsr: true });
  const width = desktop
    ? Widths.DESKTOP
    : laptop
    ? Widths.LAPTOP
    : mid
    ? Widths.MID
    : tablet
    ? Widths.TABLET
    : Widths.MOBILE;

  if (!dates) {
    return (
      <div className={classes.loadingContainer}>
        <LoadingIndicator size={56} />
      </div>
    );
  }

  const portfolioDrift = (portfolio: Portfolio, balance: number, title: string) => {
    const treshHold = newAccountsStore?.summary?.driftThreshold;
    return portfolio.drift !== undefined && balance !== 0 ? (
      <>
        <Tooltip title={t(`bucket.${title}.title`)} placement="left">
          <span style={{ whiteSpace: 'nowrap' }}>
            {portfolio.portfolio}{' '}
            <span
              style={{
                color: !treshHold
                  ? undefined
                  : portfolio.drift > treshHold[1]
                  ? EdwardColors.ALERT
                  : portfolio.drift > treshHold[0]
                  ? EdwardColors.DARK
                  : EdwardColors.GREEN,
              }}
            >
              {fractionToPercent(portfolio.drift, 0)}
            </span>
          </span>
        </Tooltip>
      </>
    ) : (
      ''
    );
  };

  return (
    <>
      <MaterialTable
        enhanced
        refresh
        data={dates}
        components={{
          Toolbar: (props) => (
            <Grid container>
              <MTableToolbar {...props} />
              {/* FIXME: c&p */}
              <div>
                <Tooltip title={t('general_resetTableCols') || ''} placement="top">
                  <div>
                    <IconButton
                      size="small"
                      onClick={() => {
                        newAccountsStore && newAccountsStore.resetColumns(AccountPageTypes.DATES);
                        setDefaultView(true);
                      }}
                    >
                      <RotateLeftIcon fontSize="large" />
                    </IconButton>
                  </div>
                </Tooltip>
                <Tooltip title={t('general_show_filters') || ''} placement="top">
                  <div>
                    <IconButton size="small" onClick={toggleFilterVisibility}>
                      <FilterIcon transparentBgr defaultFill fontSize="large" strokeWidth={0.1} />
                    </IconButton>
                  </div>
                </Tooltip>
                <Button variant="outlined" onClick={downloadAccounts} startIcon={<GetAppIcon />}>
                  {t('accounts_download_csv_short')}
                </Button>
              </div>
            </Grid>
          ),
        }}
        columns={sortColumns(
          defaultView,
          AccountPageTypes.DATES,
          [
            {
              hiddenByColumnsButton: true,
              title: t('general_account'),
              field: 'clientName',
              cellStyle: width < Widths.DESKTOP ? undefined : { width: '25%' },
              render: ({ clientName, accountName }) => (
                <>
                  <span className={classes.name}>{clientName}</span>
                  <span className={classes.secondLine}>({accountName})</span>
                </>
              ),
              customFilterAndSearch: (filter, rowData, def) =>
                searchInStrings(filter, [rowData.clientName, rowData.accountName]),
              customSort: (a, b) => {
                return `${a.clientName} ${a.accountName}`.localeCompare(`${b.clientName} ${b.accountName}`);
              },
            },
            {
              hidden: defaultView && width < Widths.LAPTOP,
              title: t('accounts_totalAum'),
              field: 'totalAum',
              cellStyle: { ...rightCellStyles },
              headerStyle: { textAlign: 'right' },
              render: ({ totalAum, currency }) => formatAmountValue(totalAum, currency),
              customSort: (a, b) =>
                compareAmountValues(
                  { value: a.totalAum, currency: a.currency },
                  { value: b.totalAum, currency: b.currency },
                  newAccountsStore?.summary?.conversionRate,
                ),
            },
            {
              title: t('accounts_status'),
              field: 'status',
              cellStyle: { ...centerCellStyles },
              headerStyle: { textAlign: 'center' },
              customFilterAndSearch: (search, data) => {
                return (
                  t(`options_accountStatus_${data.status}${data.processing ? '_processing' : ''}`)
                    .toLowerCase()
                    .indexOf(search.trim().toLowerCase()) > -1
                );
              },
              render: ({ status, processing, completedOn }) => (
                <Tooltip title={t(`accounts_tooltip_${status || ''}`) || ''} placement="top">
                  <div>
                    <span className={`${classes.status} status_${status}`}>
                      {t(`options_accountStatus_${status}${processing ? '_processing' : ''}`)}
                    </span>
                    <span className={classes.secondLine}>
                      ({t('general_since')} {completedOn})
                    </span>
                  </div>
                </Tooltip>
              ),
              customSort: (a, b) => {
                const statusSort = t(
                  `options_accountStatus_${a.status}${a.processing ? '_processing' : ''}`,
                ).localeCompare(t(`options_accountStatus_${b.status}${b.processing ? '_processing' : ''}`));
                return statusSort === 0 ? a.aum - b.aum : statusSort;
              },
            },
            {
              hidden: defaultView && width < Widths.TABLET,
              title: t('accounts_lastPayment'),
              field: 'lastPaymentDate',
              cellStyle: { ...centerCellStyles },
              headerStyle: { textAlign: 'center' },
              customSort: (a, b) => compareCzechDates(a.lastPaymentDate, b.lastPaymentDate),
              customFilterAndSearch: (filter, rowData, def) =>
                searchInStrings(filter, [rowData.lastPaymentDate, rowData.lastPaymentValue]),
              render: ({ lastPaymentDate, lastPaymentValue, currency }) => (
                <>
                  {lastPaymentDate || '—'}
                  <span className={classes.secondLine}>{formatAmountValue(lastPaymentValue, currency)}</span>
                </>
              ),
            },
            {
              hidden: defaultView && width < Widths.DESKTOP,
              field: 'expectedDeposits',
              title: (
                <>
                  {t('accounts_expectedDeposits')}
                  <Info content={t('tooltip_accounts_expectedDeposits')} inline />
                </>
              ),
              cellStyle: { ...rightCellStyles },
              headerStyle: { textAlign: 'right' },
              render: ({ expectedDeposits, currency, accountId }) => (
                <Tooltip title={t('tooltip_accounts_expectedDepositsLink') || ''} placement="top">
                  <span
                    className={`${classes.balance} ${
                      expectedDeposits !== 0 ? 'negative_' + (expectedDeposits < 0) : ''
                    }`}
                    style={{ textDecoration: 'underline' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpectedDepositsId(accountId);
                    }}
                  >
                    {expectedDeposits === 0 ? t('general_OK') : formatAmountValue(expectedDeposits, currency, true)}
                  </span>
                </Tooltip>
              ),
              customSort: (a, b) =>
                compareAmountValues(
                  { value: a.expectedDeposits, currency: a.currency },
                  { value: b.expectedDeposits, currency: b.currency },
                  newAccountsStore?.summary?.conversionRate,
                ),
            },
            {
              title: (
                <>
                  {t('accounts_drift')}
                  <Info
                    inline
                    content={
                      <span
                        dangerouslySetInnerHTML={{
                          __html: t(`tooltip_accounts_drift`, {
                            interpolation: { escapeValue: false },
                          }),
                        }}
                      />
                    }
                  />
                </>
              ),
              field: 'drift',
              cellStyle: { ...centerCellStyles },
              headerStyle: { textAlign: 'center' },
              customSort: (a, b) => {
                const aMax = Math.max(
                  a.portfolioShortBalance !== 0 ? a.portfolioShort?.drift || 0 : 0,
                  a.portfolioMidBalance !== 0 ? a.portfolioMid?.drift || 0 : 0,
                  a.portfolioLongBalance !== 0 ? a.portfolioLong?.drift || 0 : 0,
                );
                const bMax = Math.max(
                  b.portfolioShortBalance !== 0 ? b.portfolioShort?.drift || 0 : 0,
                  b.portfolioMidBalance !== 0 ? b.portfolioMid?.drift || 0 : 0,
                  b.portfolioLongBalance !== 0 ? b.portfolioLong?.drift || 0 : 0,
                );
                return aMax - bMax;
              },
              render: ({
                portfolioShort,
                portfolioMid,
                portfolioLong,
                portfolioShortBalance,
                portfolioMidBalance,
                portfolioLongBalance,
              }) => (
                <>
                  {portfolioDrift(portfolioLong, portfolioLongBalance, 'long_term')}
                  <br />
                  {portfolioDrift(portfolioMid, portfolioMidBalance, 'mid_term')}
                  <br />
                  {portfolioDrift(portfolioShort, portfolioShortBalance, 'short_term')}
                </>
              ),
            },
            {
              title: t('accounts_closestTarget'),
              field: 'closestTargetDate',
              cellStyle: { ...centerCellStyles },
              headerStyle: { textAlign: 'center' },
              customSort: (a, b) => {
                return compareAccountDates(a.closestTargetDate, b.closestTargetDate);
              },
              render: ({ closestTargetReadable, closestTargetValue, closestTargetSeverity, currency }) => (
                <>
                  <span className={`${classes.severity} severity_${closestTargetSeverity}`}>
                    {closestTargetReadable || '—'}
                  </span>
                  <span className={classes.secondLine}>
                    {closestTargetReadable && formatAmountValue(closestTargetValue, currency)}
                  </span>
                </>
              ),
            },
            {
              hidden: defaultView && width < Widths.TABLET,
              title: t('accounts_closestDeposit'),
              field: 'closestDepositDate',
              cellStyle: { ...centerCellStyles },
              headerStyle: { textAlign: 'center' },
              customSort: (a, b) => compareAccountDates(a.closestDepositDate, b.closestDepositDate),
              render: ({
                closestDepositDate,
                closestDepositReadable,
                closestDepositValue,
                closestDepositSeverity,
                currency,
              }) => (
                <>
                  <span className={`${classes.severity} severity_${closestDepositSeverity}`}>
                    {closestDepositReadable || '—'}
                  </span>
                  <span className={classes.secondLine}>
                    {closestDepositReadable && formatAmountValue(closestDepositValue, currency)}
                  </span>
                </>
              ),
            },
            {
              hidden: defaultView && width < Widths.LAPTOP,
              title: t('accounts_FQValidity'),
              field: 'financialQuestionnaireValidityDate',
              cellStyle: { ...centerCellStyles },
              headerStyle: { textAlign: 'center' },
              customSort: (a, b) =>
                compareCzechDates(a.financialQuestionnaireValidityDate, b.financialQuestionnaireValidityDate),

              render: ({ financialQuestionnaireValidityDate, financialQuestionnaireValiditySeverity }) => (
                <span
                  className={`${classes.severity} severity_${financialQuestionnaireValiditySeverity} ${classes.noWrap}`}
                >
                  {overdue.includes(financialQuestionnaireValiditySeverity) ? <NotificationImportantIcon /> : ''}
                  {financialQuestionnaireValidityDate || '—'}
                </span>
              ),
            },
            {
              hidden: defaultView && width < Widths.LAPTOP,
              title: t('accounts_IQValidity'),
              field: 'investmentQuestionnaireValidityDate',
              cellStyle: { ...centerCellStyles },
              headerStyle: { textAlign: 'center' },
              customSort: (a, b) =>
                compareCzechDates(a.investmentQuestionnaireValidityDate, b.investmentQuestionnaireValidityDate),

              render: ({ investmentQuestionnaireValidityDate, investmentQuestionnaireValiditySeverity }) => (
                <span
                  className={`${classes.severity} severity_${investmentQuestionnaireValiditySeverity} ${classes.noWrap}`}
                >
                  {overdue.includes(investmentQuestionnaireValiditySeverity) ? <NotificationImportantIcon /> : ''}
                  {investmentQuestionnaireValidityDate || '—'}
                </span>
              ),
            },
            {
              hidden: defaultView && (width < Widths.MID || width >= Widths.LAPTOP),
              title: t('accounts_FQorIQValidity'),
              field: 'fqOrIqValidityDate',
              cellStyle: { ...centerCellStyles },
              headerStyle: { textAlign: 'center' },
              customSort: (a, b) => compareCzechDates(a.fqOrIqValidityDate, b.fqOrIqValidityDate),

              render: ({ fqOrIqValidityDate, fqOrIqValiditySeverity }) => (
                <span className={`${classes.severity} severity_${fqOrIqValiditySeverity} ${classes.noWrap}`}>
                  {overdue.includes(fqOrIqValiditySeverity) ? <NotificationImportantIcon /> : ''}
                  {fqOrIqValidityDate || '—'}
                </span>
              ),
            },
            {
              hidden: defaultView && width < Widths.LAPTOP,
              hiddenByColumnsButton: true,
              field: 'actions',
              render: (account) => {
                return (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                    }}
                  >
                    {width >= Widths.MID && (
                      <Tooltip
                        title={t(account.hasNotes ? 'accountNotes_title' : 'accountNotes_add') || ''}
                        placement="top"
                      >
                        <IconButton
                          size="medium"
                          onClick={(event) => {
                            event.stopPropagation();
                            rootStore!.stores.uiStore.openNotes(account.accountId);
                          }}
                        >
                          {account.hasNotes ? (
                            <NoteIcon
                              fontSize="small"
                              htmlColor={EdwardColors.SECONDARY}
                              defaultFill
                              transparentBgr
                              strokeWidth={0}
                            />
                          ) : (
                            <NoteIcon
                              fontSize="small"
                              htmlColor={EdwardColors.GRAY_DARKER}
                              defaultFill
                              transparentBgr
                              strokeWidth={0}
                            />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                    {account.status === AccountStatus.ACTIVE && (
                      <Tooltip title={t('accounts_detail') || ''} placement="top">
                        <IconButton
                          size="medium"
                          component={Link}
                          className="accountDetail-button"
                          to={routes.ACCOUNTS_DETAIL(account.accountId)}
                        >
                          <DetailIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                );
              },
              cellStyle: {
                width: width > Widths.LAPTOP ? '10%' : '5%',
              },
            },
          ],
          Order.DESC,
          1,
        )}
        options={{
          searchFieldAlignment: 'left',
          headerStyle: {
            borderBottom: '2px solid rgb(224, 224, 224)',
          },
          pageSize: Math.min(pageSize, 50), //EDS-3894
          filtering: filtersVisible,
          sorting: true,
          searchText: state.search,
          columnsButton: true,
        }}
        onSearchChange={(search) => {
          onTableSearchChange(AccountPageTypes.DATES, search);
        }}
        onOrderChange={(orderedColumnId, orderDirection) =>
          onTableOrderChange(AccountPageTypes.DATES, orderedColumnId, orderDirection as Order)
        }
        onColumnDragged={(sourceIndex: number, destinationIndex: number) =>
          onColumnDragged(AccountPageTypes.DATES, sourceIndex, destinationIndex)
        }
        onChangeColumnHidden={(column: Column<any>, hidden: boolean) =>
          onChangeColumnHidden(AccountPageTypes.DATES, column.field, hidden, true)
        }
        onRowClick={(_event, { accountId }) => {
          history.push(routes.ACCOUNTS_DETAIL(accountId));
        }}
        onRowsPerPageChange={onChangeRowsPerPage}
      />
      <GenerationDialog store={newAccountsStore!} />

      {expectedDepositsId && (
        <ExpectedDeposits
          accountId={expectedDepositsId}
          handleClose={() => setExpectedDepositsId(null)}
          handleRefresh={() => reloadAccounts('ACTIVE')}
        />
      )}
    </>
  );
};

export default inject('newAccountsStore', 'rootStore')(observer(ImportantDates));
